/* eslint-disable @typescript-eslint/no-explicit-any */
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { WizardView } from './Internal/WizardView';
/**
 *
 * High-level wizard controller that manages step state, data aggregation, and final submission.
 * Renders a {@link WizardView} for the active step, coordinates submit() calls on the step component,
 * merges step data across the flow, and invokes onSubmit when the last step completes.
 * @template {WidgetConfigBase} [Config=WidgetFormConfig] - Root widget config type used by the step definitions.
 * @param {object} root0 - Component props.
 * @param {WizardStep<Config>[]} root0.steps - Ordered list of steps (each step provides a label and either a form config or a custom render).
 * @param {'vertical'|'horizontal'} [root0.orientation='vertical'] - Stepper orientation; impacts layout and navigation footer placement.
 * @param {string} [root0.submitButtonLabel='Submit'] - Custom label for the final submit button on the last step.
 * @param {any} [root0.data] - Optional initial aggregated data passed into the first step.
 * @param {(changed: boolean) => void} [root0.onChanged] - Called when a step reports data changes; receives true when dirty.
 * @param {(data: any) => Promise<any>} root0.onSubmit - Invoked with the aggregated data when the final step completes; resolves with a submission result.
 * @param {(data: any, response: any) => void} [root0.onSubmitSuccess] - Called after onSubmit resolves, with submitted data and backend response.
 * @param {(data: any) => void} [root0.onDataChange] - Called whenever the current step’s data changes; receives the step-local data snapshot.
 * @param {boolean} [root0.addFirstStepBack] - If true, shows a “Back” action on the first step (e.g., to exit or return to a parent view).
 * @param {string} [root0.firstStepBackButtonLabel='Back'] - Label for the first-step back button when addFirstStepBack is enabled.
 * @param {() => void} [root0.onFirstStepBack] - Handler for the first-step back action.
 *@returns {React.ReactElement | null} The rendered wizard view when steps are provided; otherwise null.
 */
export function Wizard({ steps, orientation, submitButtonLabel, data: initialData, onChanged, onSubmit, onSubmitSuccess, onDataChange, addFirstStepBack, firstStepBackButtonLabel, onFirstStepBack, }) {
    const stepComponentRef = React.useRef(null);
    const [state, setState] = React.useState({ step: 0, data: initialData });
    const [submitting, setSubmitting] = React.useState(false);
    const { t } = useCommonFormUiTranslation();
    const handleBack = React.useCallback(() => {
        setState((prevState) => {
            var _a;
            if (prevState.step > 0) {
                return {
                    step: prevState.step - 1,
                    data: Object.assign(Object.assign({}, prevState.data), (_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.getData()),
                };
            }
            else {
                return prevState;
            }
        });
    }, []);
    const handleNext = React.useCallback(() => {
        var _a;
        (_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.submit().catch(handleErrorDialog);
    }, []);
    const handleChanged = React.useCallback(() => {
        var _a;
        if (onChanged) {
            onChanged(true);
        }
        if (onDataChange) {
            onDataChange((_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.getData());
        }
    }, [onChanged, onDataChange]);
    const handleSubmit = React.useCallback((stepData) => {
        return Promise.resolve(stepData);
    }, []);
    const handleSubmitSuccess = React.useCallback((stepData) => {
        setState((prevState) => {
            if (prevState.step < steps.length - 1) {
                return {
                    step: prevState.step + 1,
                    data: Object.assign(Object.assign({}, prevState.data), stepData),
                };
            }
            else {
                return Object.assign(Object.assign({}, prevState), { result: Object.assign(Object.assign({}, prevState.data), stepData) });
            }
        });
        if (onChanged) {
            onChanged(true);
        }
    }, [steps.length, onChanged]);
    React.useEffect(() => {
        if (state.result) {
            setSubmitting(true);
            onSubmit(state.result)
                .then((response) => {
                setSubmitting(false);
                if (onSubmitSuccess) {
                    onSubmitSuccess(state.result, response);
                }
                return response;
            })
                .catch((error) => {
                setSubmitting(false);
                handleErrorDialog(error);
            });
        }
    }, [state.result, onSubmit, onSubmitSuccess]);
    if (steps.length > 0) {
        const step = steps[state.step];
        return (React.createElement(WizardView, { steps: steps, step: step, stepComponentRef: stepComponentRef, state: state, submitting: submitting, handleBack: handleBack, handleNext: handleNext, handleChanged: handleChanged, handleSubmit: handleSubmit, handleSubmitSuccess: handleSubmitSuccess, addFirstStepBack: addFirstStepBack ? addFirstStepBack : false, onFirstStepBack: onFirstStepBack ? onFirstStepBack : () => { }, firstStepBackButtonLabel: firstStepBackButtonLabel ? firstStepBackButtonLabel : t('common.back'), submitButtonLabel: submitButtonLabel ? submitButtonLabel : t('common.submit'), orientation: orientation ? orientation : 'vertical' }));
    }
    else {
        return null;
    }
}
